import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/Dialog";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { useToast } from "../components/ui/Toast";
import { AlertCircle, Check, X } from 'lucide-react';
import { Switch } from "../components/ui/Switch";
import { useUsersApi } from '../apis/users-apis';
import { UserData } from '../types';

interface UserDetailModalProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserUpdated: () => void;
}

interface EditableValues {
  isActive: boolean;
  isAdmin: boolean;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  open,
  onOpenChange,
  onUserUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<EditableValues>({
    isActive: false,
    isAdmin: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToast();
  const { getUser, updateUser } = useUsersApi();
  
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError(null);
      try {
        const data = await getUser(userId);
        setUser(data);
        setEditedValues({
          isActive: data.isActive,
          isAdmin: data.isAdmin
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user details');
        addToast({
          title: "Error",
          description: "Failed to load user details",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (open && userId) {
      fetchUserDetails();
    }
  }, [userId, open, addToast]);

  const handleSave = async () => {
    if (!user || !userId) return;
    
    setIsSaving(true);
    try {
      await updateUser(userId, {
        isActive: editedValues.isActive,
        isAdmin: editedValues.isAdmin
      });

      addToast({
        title: "Success",
        description: "User updated successfully",
        variant: "default",
      });

      // Refresh user data
      const updatedUser = await getUser(userId);
      setUser(updatedUser);
      
      onUserUpdated();
      setIsEditing(false);
    } catch (err) {
      addToast({
        title: "Error",
        description: "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center justify-center p-6">
            <Spinner className="w-8 h-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center gap-2 text-red-500 p-4">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Email:</span>
                  <span>{user.email}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Status:</span>
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editedValues.isActive}
                        onCheckedChange={(checked) => 
                          setEditedValues(prev => ({ ...prev, isActive: checked }))
                        }
                        disabled={isSaving}
                      />
                      <span>{editedValues.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  ) : (
                    <Badge 
                      variant={user.isActive ? "default" : "secondary"}
                      className={user.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </Badge>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Role:</span>
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editedValues.isAdmin}
                        onCheckedChange={(checked) => 
                          setEditedValues(prev => ({ ...prev, isAdmin: checked }))
                        }
                        disabled={isSaving}
                      />
                      <span>{editedValues.isAdmin ? 'Admin' : 'User'}</span>
                    </div>
                  ) : (
                    <Badge 
                      variant="outline"
                      className={user.isAdmin ? "border-blue-500 text-blue-800" : "border-gray-500 text-gray-800"}
                    >
                      {user.isAdmin ? "Admin" : "User"}
                    </Badge>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Last Login:</span>
                  <span>{formatDate(user.lastLogin)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="font-medium">Created At:</span>
                  <span>{formatDate(user.createdAt)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2 pt-2">
            {isEditing ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedValues({
                      isActive: user.isActive,
                      isAdmin: user.isAdmin
                    });
                  }}
                  disabled={isSaving}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)}>
                Edit User
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailModal;